import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { v4 as uuidv4 } from 'uuid'
import localCloudService from '../services/localCloudService'
import simpleDataPersistenceService from '../services/simpleDataPersistenceServiceV2'
import imageStorageService from '../services/imageStorageService'
import enhancedStorageService from '../services/enhancedStorageService'
import storageManager from '../services/storageManager'
// 注意：这里不能直接导入useAuth，会导致循环依赖

// 使用增强存储服务（IndexedDB）- 支持更大容量，不限制单个故事大小
const USE_ENHANCED_STORAGE = true

// 获取用户特定的存储键
const getUserStorageKey = (userId) => {
  return `altverse-stories-${userId || 'guest'}`
}

// 数据序列化函数 - 保持完整数据，不进行内容限制
const serializeData = (data) => {
  try {
    // 保持所有原始数据，不进行任何内容限制
    return {
      stories: data.stories || [],
      characters: data.characters || []
    }
  } catch (error) {
    console.error('序列化数据时出错:', error)
    return data
  }
}

// 存储空间清理函数
const cleanupStorageSpace = () => {
  try {
    console.log('开始清理存储空间...')
    
    // 清理旧的用户编辑数据
    const keysToRemove = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && (key.startsWith('user-edited-') || key.startsWith('ai-story-'))) {
        keysToRemove.push(key)
      }
    }
    
    // 按时间排序，删除最旧的10个（增加清理数量）
    keysToRemove.sort().slice(0, 10).forEach(key => {
      localStorage.removeItem(key)
      console.log('🗑️ 已清理旧数据:', key)
    })
    
    // 清理可能损坏的数据
    const altverseKeys = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith('altverse-')) {
        try {
          const data = JSON.parse(localStorage.getItem(key))
          if (JSON.stringify(data).includes('NaN') || 
              JSON.stringify(data).includes('undefined') ||
              JSON.stringify(data).includes('[object Object]')) {
            altverseKeys.push(key)
          }
        } catch (error) {
          altverseKeys.push(key)
        }
      }
    }
    
    // 删除损坏的数据
    altverseKeys.forEach(key => {
      localStorage.removeItem(key)
      console.log('🗑️ 已清理损坏数据:', key)
    })
    
    // 清理过大的数据项
    const allKeys = Object.keys(localStorage)
    allKeys.forEach(key => {
      try {
        const item = localStorage.getItem(key)
        if (item && item.length > 1024 * 1024) { // 超过1MB的数据
          console.log(`🗑️ 清理过大数据项: ${key} (${(item.length / 1024 / 1024).toFixed(2)}MB)`)
          localStorage.removeItem(key)
        }
      } catch (error) {
        console.warn(`清理数据项时出错: ${key}`, error)
      }
    })
    
    console.log('存储空间清理完成')
  } catch (error) {
    console.error('清理存储空间时出错:', error)
  }
}

// 故事状态管理
export const useStoryStore = create(
  persist(
    (set, get) => ({
      // 状态
      stories: [],
      currentStory: null,
      characters: [],
      chapters: [],
      currentUserId: null,
      
      // 初始化存储
      initializeStore: async () => {
        try {
          console.log('🚀 开始初始化存储...')
          
          // 获取当前用户ID - 从localStorage获取，避免循环依赖
          let userId = 'guest'
          try {
            const authData = localStorage.getItem('altverse-auth-store')
            if (authData) {
              const parsed = JSON.parse(authData)
              userId = parsed?.state?.user?.id || 'guest'
            }
          } catch (error) {
            console.warn('无法获取用户ID，使用访客模式:', error)
          }
          
          // 更新当前用户ID
          set({ currentUserId: userId })
          console.log(`👤 当前用户: ${userId}`)
          
          // 1. 优先恢复最近创建的故事快照（防止刷新丢失）
          const restoredFromSnapshot = new Set()
          try {
            const lastCreatedSnapshot = sessionStorage.getItem('altverse-last-created-story')
            if (lastCreatedSnapshot) {
              const snapshot = JSON.parse(lastCreatedSnapshot)
              const age = Date.now() - snapshot.timestamp
              
              if (age < 10 * 60 * 1000) { // 延长到10分钟
                console.log('🔄 发现最近创建的故事快照，立即恢复到内存')
                set((state) => ({
                  stories: [snapshot.story, ...state.stories.filter(s => s.id !== snapshot.story.id)]
                }))
                restoredFromSnapshot.add(snapshot.story.id)
                console.log('✅ 已从快照恢复故事:', snapshot.story.id, snapshot.story.title)
                
                // 异步保存到IndexedDB
                if (USE_ENHANCED_STORAGE) {
                  enhancedStorageService.saveStory(snapshot.story)
                    .then(() => console.log('✅ 快照已保存到IndexedDB'))
                    .catch(err => console.error('快照保存失败:', err))
                }
              } else {
                console.log('⚠️ 快照已过期，清理...')
                sessionStorage.removeItem('altverse-last-created-story')
              }
            }
          } catch (error) {
            console.error('恢复快照失败:', error)
          }
          
          // 2. 然后继续正常的IndexedDB加载流程
          // 优先使用增强存储服务（IndexedDB）
          if (USE_ENHANCED_STORAGE) {
            try {
              const stories = await enhancedStorageService.getAllStories(userId)
              if (stories && stories.length > 0) {
                // 合并快照恢复的故事和IndexedDB中的故事
                const currentStories = get().stories
                const mergedStories = [...currentStories]
                
                // 添加IndexedDB中的故事，但不覆盖已从快照恢复的
                stories.forEach(story => {
                  if (!restoredFromSnapshot.has(story.id)) {
                    const existingIndex = mergedStories.findIndex(s => s.id === story.id)
                    if (existingIndex >= 0) {
                      // 如果已存在，比较时间戳，使用更新的
                      if (new Date(story.updatedAt) > new Date(mergedStories[existingIndex].updatedAt)) {
                        mergedStories[existingIndex] = story
                      }
                    } else {
                      mergedStories.push(story)
                    }
                  }
                })
                
                set({
                  stories: mergedStories,
                  characters: [],
                  chapters: []
                })
                console.log(`✅ 从 IndexedDB 加载了 ${stories.length} 个故事，合并后共 ${mergedStories.length} 个故事`)
                
                // 启动定期自动备份（每5分钟）
                get().startAutoBackup()
                
                return
              }
            } catch (error) {
              console.warn('⚠️ IndexedDB 加载失败，尝试从备份恢复:', error)
              
              // 尝试从会话备份恢复
              const restoreResult = await enhancedStorageService.restoreFromSessionBackup()
              if (restoreResult.success) {
                console.log('✅ 从会话备份恢复:', restoreResult.message)
                // 重新加载
                const stories = await enhancedStorageService.getAllStories(userId)
                const currentStories = get().stories
                const mergedStories = [...currentStories]
                
                stories.forEach(story => {
                  if (!restoredFromSnapshot.has(story.id)) {
                    const existingIndex = mergedStories.findIndex(s => s.id === story.id)
                    if (existingIndex >= 0) {
                      mergedStories[existingIndex] = story
                    } else {
                      mergedStories.push(story)
                    }
                  }
                })
                
                set({
                  stories: mergedStories,
                  characters: [],
                  chapters: []
                })
                get().startAutoBackup()
                return
              }
            }
          }
          
          // 尝试从持久化存储加载数据
          const persistentResult = await simpleDataPersistenceService.loadData(`user_data_${userId}`, { userId })
          
          if (persistentResult.success && persistentResult.data) {
            const data = persistentResult.data
            set({
              stories: data.stories || [],
              characters: data.characters || [],
              chapters: data.chapters || []
            })
            console.log(`✅ 已从持久化存储加载用户 ${userId} 的数据`)
          } else {
            // 回退到localStorage
            const userStorageKey = getUserStorageKey(userId)
            const savedData = localStorage.getItem(userStorageKey)
            
            if (savedData) {
              try {
                const parsedData = JSON.parse(savedData)
                set({
                  stories: parsedData.stories || [],
                  characters: parsedData.characters || [],
                  chapters: parsedData.chapters || []
                })
                console.log(`✅ 已从localStorage加载用户 ${userId} 的数据`)
              } catch (error) {
                console.error('❌ 解析localStorage数据失败:', error)
                set({
                  stories: [],
                  characters: [],
                  chapters: []
                })
              }
            } else {
              console.log(`📝 用户 ${userId} 暂无数据，使用空数据`)
              set({
                stories: [],
                characters: [],
                chapters: []
              })
            }
          }
          
          // 启动数据同步
          simpleDataPersistenceService.syncData(userId)
          
          // 启动定期自动备份
          if (USE_ENHANCED_STORAGE) {
            get().startAutoBackup()
          }
          
          // 异步恢复，不阻塞初始化
          get().afterLoad().catch(err => {
            console.error('恢复备份失败（不影响使用）:', err)
          })
        } catch (error) {
          console.error('❌ 初始化存储失败:', error)
        }
      },
      
      // 自动备份定时器
      autoBackupInterval: null,
      
      // 启动定期自动备份
      startAutoBackup: () => {
        const interval = get().autoBackupInterval
        
        // 清除已存在的定时器
        if (interval) {
          clearInterval(interval)
        }
        
        // 每5分钟执行一次自动备份
        const newInterval = setInterval(async () => {
          if (USE_ENHANCED_STORAGE) {
            try {
              await enhancedStorageService.autoBackup()
              console.log('🔄 自动备份完成')
            } catch (error) {
              console.error('❌ 自动备份失败:', error)
            }
          }
        }, 5 * 60 * 1000) // 5分钟
        
        set({ autoBackupInterval: newInterval })
        console.log('✅ 已启动自动备份（每5分钟）')
        
        // 立即执行一次备份
        if (USE_ENHANCED_STORAGE) {
          enhancedStorageService.autoBackup().catch(err => {
            console.error('初始备份失败:', err)
          })
        }
      },
      
      // 停止自动备份
      stopAutoBackup: () => {
        const interval = get().autoBackupInterval
        if (interval) {
          clearInterval(interval)
          set({ autoBackupInterval: null })
          console.log('⏹️ 已停止自动备份')
        }
      },
      
      // 页面卸载前保存所有未保存的数据
      beforeUnload: () => {
        try {
          const { stories, currentUserId } = get()
          
          // 保存当前状态到sessionStorage（作为紧急备份）
          const emergencyBackup = {
            timestamp: Date.now(),
            userId: currentUserId,
            stories: stories,
            characters: get().characters,
            chapters: get().chapters
          }
          
          sessionStorage.setItem('altverse-emergency-backup', JSON.stringify(emergencyBackup))
          console.log(`💾 紧急备份已保存 (${stories.length} 个故事)`)
          
          // 停止自动备份定时器
          get().stopAutoBackup()
        } catch (error) {
          console.error('紧急备份失败:', error)
        }
      },
      
      // 页面加载后恢复数据（异步，不阻塞）
      afterLoad: async () => {
        try {
          const emergencyBackup = sessionStorage.getItem('altverse-emergency-backup')
          if (emergencyBackup) {
            const parsed = JSON.parse(emergencyBackup)
            const age = Date.now() - parsed.timestamp
            
            // 只恢复5分钟内的备份
            if (age < 5 * 60 * 1000) {
              console.log(`🔄 发现紧急备份 (${parsed.stories.length} 个故事)`)
              
              // 检查是否需要恢复
              const currentStories = get().stories
              if (currentStories.length < parsed.stories.length) {
                console.log('⚠️ 检测到数据可能丢失，从紧急备份恢复...')
                
                // 立即恢复状态（同步）
                set({
                  stories: parsed.stories,
                  characters: parsed.characters || [],
                  chapters: parsed.chapters || []
                })
                
                console.log('✅ 从紧急备份恢复成功（内存）')
                
                // 后台异步保存到IndexedDB，不等待
                if (USE_ENHANCED_STORAGE) {
                  for (const story of parsed.stories) {
                    enhancedStorageService.saveStory(story, { retryCount: 1, timeout: 60000 })
                      .catch(error => {
                        console.error('后台保存恢复的故事失败:', story.id, error)
                      })
                  }
                }
              } else {
                console.log('✅ 数据完整，无需恢复')
              }
            } else {
              console.log('⚠️ 紧急备份过期，已清除')
            }
            
            // 清除备份
            sessionStorage.removeItem('altverse-emergency-backup')
          }
        } catch (error) {
          console.error('恢复紧急备份失败:', error)
        }
      },
      
      // 保存用户数据到本地存储
      saveUserData: async () => {
        try {
          const { stories, characters, chapters, currentUserId } = get()
          
          // 使用新的数据持久化服务
          const data = {
            stories,
            characters,
            chapters,
            lastSaved: new Date().toISOString()
          }
          
          // 保存到持久化存储
          await simpleDataPersistenceService.saveData(`user_data_${currentUserId}`, data, {
            userId: currentUserId,
            sync: true
          })
          
          // 保持localStorage作为备份
          const userStorageKey = getUserStorageKey(currentUserId)
          localStorage.setItem(userStorageKey, JSON.stringify(data))
          
          console.log(`💾 已保存用户 ${currentUserId} 的数据`)
        } catch (error) {
          console.error('❌ 保存用户数据失败:', error)
        }
      },
      
      // 切换用户数据
      switchUserData: async (userId) => {
        try {
          // 先保存当前用户数据
          get().saveUserData()
          
          // 切换到新用户数据
          set({ currentUserId: userId })
          await get().initializeStore()
          
          console.log(`🔄 已切换到用户 ${userId} 的数据`)
        } catch (error) {
          console.error('❌ 切换用户数据失败:', error)
        }
      },
      
      // 故事管理（同步返回，异步保存）
      createStory: (storyData) => {
        const newStory = {
          id: uuidv4(),
          title: storyData.title,
          description: storyData.description,
          genre: storyData.genre,
          theme: storyData.theme,
          style: storyData.style,
          content: storyData.content,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          status: 'draft', // draft, published
          tags: storyData.tags || [], // 标签
          nodes: storyData.nodes || [],
          edges: storyData.edges || [],
          characters: storyData.characters || [],
          scenes: storyData.scenes || [],
          chapters: storyData.chapters || [],
          metadata: storyData.metadata || {},
          userId: get().currentUserId || 'guest'
        }
        
        // 先更新状态，立即返回，不等待存储完成（避免UI卡死）
        set((state) => ({
          stories: [...state.stories, newStory],
          currentStory: newStory
        }))
        
        // 使用新的 StorageManager 进行异步保存（完全非阻塞）
        // StorageManager 内置内存缓存和后台保存队列
        storageManager.saveStory(newStory).catch(err => {
          console.error('⚠️ StorageManager 保存失败（不影响使用）:', err)
        })
        
        // 同时使用 enhancedStorageService 作为备份
        if (USE_ENHANCED_STORAGE) {
          enhancedStorageService.saveStory(newStory).catch(err => {
            console.error('⚠️ enhancedStorageService 备份保存失败（不影响使用）:', err)
          })
        }
        
        // 立即保存紧急快照到sessionStorage（同步，快速）
        try {
          const emergencySnapshot = {
            timestamp: Date.now(),
            story: newStory
          }
          sessionStorage.setItem('altverse-last-created-story', JSON.stringify(emergencySnapshot))
          console.log('💾 已保存创建快照:', newStory.id)
        } catch (e) {
          console.warn('快照保存失败（不影响使用）:', e)
        }
        
        // 立即返回新故事，不等待存储完成
        return newStory
      },
      
      updateStory: async (storyId, updates) => {
        // 先更新状态，立即返回，不等待存储完成（避免UI卡死）
        set((state) => {
          const updatedStories = state.stories.map(story => 
            story.id === storyId 
              ? { ...story, ...updates, updatedAt: new Date().toISOString() }
              : story
          )
          
          const updatedCurrentStory = get().currentStory?.id === storyId 
            ? { ...get().currentStory, ...updates, updatedAt: new Date().toISOString() }
            : get().currentStory
            
          return {
            stories: updatedStories,
            currentStory: updatedCurrentStory
          }
        })
        
        // 使用新的 StorageManager 进行异步保存（完全非阻塞）
        const updatedStory = get().stories.find(s => s.id === storyId)
        if (updatedStory) {
          storageManager.saveStory(updatedStory).catch(err => {
            console.error('⚠️ StorageManager 保存失败（不影响使用）:', err)
          })
          
          // 同时使用 enhancedStorageService 作为备份
          if (USE_ENHANCED_STORAGE) {
            enhancedStorageService.saveStory(updatedStory).catch(err => {
              console.error('⚠️ enhancedStorageService 备份保存失败（不影响使用）:', err)
            })
          }
        }
      },
      
      getStory: (storyId) => {
        const { stories } = get()
        return stories.find(story => story.id === storyId) || null
      },
      
      deleteStory: async (storyId) => {
        set((state) => ({
          stories: state.stories.filter(story => story.id !== storyId),
          currentStory: get().currentStory?.id === storyId ? null : get().currentStory
        }))
        
        // 从 IndexedDB 或 localStorage 中删除
        if (USE_ENHANCED_STORAGE) {
          try {
            await enhancedStorageService.deleteStory(storyId)
            console.log('✅ 故事已从 IndexedDB 删除:', storyId)
          } catch (error) {
            console.warn('⚠️ IndexedDB 删除失败，更新 localStorage')
            get().saveLegacyStorage(null) // 保存剩余故事
          }
        } else {
          get().saveLegacyStorage(null) // 保存剩余故事
        }
      },
      
      setCurrentStory: (story) => {
        set({ currentStory: story })
      },
      
      // 节点管理
      addNode: async (storyId, nodeData) => {
        const newNode = {
          id: uuidv4(),
          type: 'storyNode',
          position: nodeData.position || { x: 0, y: 0 },
          data: {
            title: nodeData.title || '新镜头',
            description: nodeData.description || '',
            backgroundImage: nodeData.backgroundImage || '',
            imagePrompt: nodeData.imagePrompt || '',
            sceneImage: nodeData.sceneImage || '',
            imageFixed: nodeData.imageFixed || false, // 图片是否已固定，不会被系统替换
            imageSource: nodeData.imageSource || 'none', // 图片来源：'user', 'ai', 'system', 'none'
            dialogues: nodeData.dialogues || [],
            choices: nodeData.choices || [],
            chapter: nodeData.chapter || 1,
            scene: nodeData.scene || 1
          }
        }
        
        set((state) => ({
          stories: state.stories.map(story => 
            story.id === storyId 
              ? { ...story, nodes: [...story.nodes, newNode] }
              : story
          ),
          currentStory: get().currentStory?.id === storyId 
            ? { ...get().currentStory, nodes: [...get().currentStory.nodes, newNode] }
            : get().currentStory
        }))
        
        // 使用统一保存方法
        await get().saveStoryToStorage(storyId)
        return newNode
      },
      
      updateNode: async (storyId, nodeId, updates) => {
        set((state) => ({
          stories: state.stories.map(story => 
            story.id === storyId 
              ? {
                  ...story,
                  nodes: story.nodes.map(node => 
                    node.id === nodeId 
                      ? { ...node, data: { ...node.data, ...updates } }
                      : node
                  )
                }
              : story
          ),
          currentStory: get().currentStory?.id === storyId 
            ? {
                ...get().currentStory,
                nodes: get().currentStory.nodes.map(node => 
                  node.id === nodeId 
                    ? { ...node, data: { ...node.data, ...updates } }
                    : node
                )
              }
            : get().currentStory
        }))
        
        // 使用统一保存方法
        await get().saveStoryToStorage(storyId)
      },
      
      deleteNode: async (storyId, nodeId) => {
        set((state) => ({
          stories: state.stories.map(story => 
            story.id === storyId 
              ? {
                  ...story,
                  nodes: story.nodes.filter(node => node.id !== nodeId),
                  edges: story.edges.filter(edge => 
                    edge.source !== nodeId && edge.target !== nodeId
                  )
                }
              : story
          ),
          currentStory: get().currentStory?.id === storyId 
            ? {
                ...get().currentStory,
                nodes: get().currentStory.nodes.filter(node => node.id !== nodeId),
                edges: get().currentStory.edges.filter(edge => 
                  edge.source !== nodeId && edge.target !== nodeId
                )
              }
            : get().currentStory
        }))
        
        // 使用统一保存方法
        await get().saveStoryToStorage(storyId)
      },
      
      // 边管理
      addEdge: async (storyId, edgeData) => {
        const newEdge = {
          id: uuidv4(),
          source: edgeData.source,
          target: edgeData.target,
          type: 'smoothstep',
          animated: true,
          style: { stroke: '#3b82f6', strokeWidth: 2 }
        }
        
        set((state) => ({
          stories: state.stories.map(story => 
            story.id === storyId 
              ? { ...story, edges: [...story.edges, newEdge] }
              : story
          ),
          currentStory: get().currentStory?.id === storyId 
            ? { ...get().currentStory, edges: [...get().currentStory.edges, newEdge] }
            : get().currentStory
        }))
        
        // 使用统一保存方法
        await get().saveStoryToStorage(storyId)
        return newEdge
      },
      
      // 角色管理
      addCharacter: (characterData) => {
        const newCharacter = {
          id: uuidv4(),
          name: characterData.name,
          appearance: characterData.appearance || '',
          background: characterData.background || '',
          image: characterData.image || '',
          createdAt: new Date().toISOString()
        }
        
        set((state) => ({
          characters: [...state.characters, newCharacter]
        }))
        
        localStorage.setItem('altverse-characters', JSON.stringify(get().characters))
        return newCharacter
      },
      
      updateCharacter: (characterId, updates) => {
        set((state) => ({
          characters: state.characters.map(character => 
            character.id === characterId 
              ? { ...character, ...updates }
              : character
          )
        }))
        
        localStorage.setItem('altverse-characters', JSON.stringify(get().characters))
      },
      
      deleteCharacter: (characterId) => {
        set((state) => ({
          characters: state.characters.filter(character => character.id !== characterId)
        }))
        
        localStorage.setItem('altverse-characters', JSON.stringify(get().characters))
      },
      
      // 章节管理
      addChapter: async (storyId, chapterData) => {
        const newChapter = {
          id: uuidv4(),
          title: chapterData.title,
          description: chapterData.description,
          scenes: chapterData.scenes || [],
          endings: chapterData.endings || [],
          order: chapterData.order || 1
        }
        
        set((state) => ({
          stories: state.stories.map(story => 
            story.id === storyId 
              ? { ...story, chapters: [...story.chapters, newChapter] }
              : story
          ),
          currentStory: get().currentStory?.id === storyId 
            ? { ...get().currentStory, chapters: [...get().currentStory.chapters, newChapter] }
            : get().currentStory
        }))
        
        // 使用统一保存方法
        await get().saveStoryToStorage(storyId)
        return newChapter
      },
      
      // 发布故事
      publishStory: async (storyId) => {
        set((state) => ({
          stories: state.stories.map(story => 
            story.id === storyId 
              ? { ...story, status: 'published', publishedAt: new Date().toISOString() }
              : story
          ),
          currentStory: get().currentStory?.id === storyId 
            ? { ...get().currentStory, status: 'published', publishedAt: new Date().toISOString() }
            : get().currentStory
        }))
        
        // 使用统一保存方法
        await get().saveStoryToStorage(storyId)
      },
      
      // 传统 localStorage 存储方式（降级方案）
      saveLegacyStorage: (storyId) => {
        try {
          const currentStories = get().stories
          // 清理base64图片
          let cleanStories = currentStories.map(story => imageStorageService.cleanStoryImages(story))
          
          const dataString = JSON.stringify(cleanStories)
          const dataSize = new Blob([dataString]).size
          
          console.log(`📊 尝试保存到 localStorage: ${imageStorageService.formatSize(dataSize)}`)
          
          // 如果太大，只保存当前故事（如果有指定storyId）
          if (dataSize > 4 * 1024 * 1024 && storyId) { // 4MB
            console.warn('⚠️ 数据过大，只保存当前故事')
            const currentStory = cleanStories.find(s => s.id === storyId)
            if (currentStory) {
              localStorage.setItem('altverse-stories', JSON.stringify([currentStory]))
              console.log('💾 已保存当前故事到 localStorage')
            }
          } else {
            localStorage.setItem('altverse-stories', dataString)
            console.log('💾 已保存所有故事到 localStorage')
          }
        } catch (error) {
          console.error('❌ localStorage 保存失败:', error)
          if (error.name === 'QuotaExceededError') {
            console.warn('⚠️ localStorage 空间不足，执行清理...')
            cleanupStorageSpace()
          }
        }
      },
      
      // 统一保存方法 - 自动使用 IndexedDB 或降级到 localStorage
      saveStoryToStorage: async (storyId) => {
        if (USE_ENHANCED_STORAGE) {
          try {
            const story = get().stories.find(s => s.id === storyId)
            if (story) {
              await enhancedStorageService.saveStory(story)
              console.log('✅ 故事已保存到 IndexedDB:', storyId)
            }
          } catch (error) {
            console.warn('⚠️ IndexedDB 保存失败，降级到 localStorage')
            get().saveLegacyStorage(storyId)
          }
        } else {
          get().saveLegacyStorage(storyId)
        }
      }
    }),
    {
      name: 'altverse-story-store',
      partialize: (state) => ({ 
        stories: state.stories,
        characters: state.characters
      }),
      // 自定义存储处理，处理配额超出错误
      storage: {
        getItem: async (name) => {
          try {
            // 策略1: 优先从sessionStorage读取（快速，最新数据）
            const sessionItem = sessionStorage.getItem(name)
            if (sessionItem) {
              console.log(`✅ 从sessionStorage读取: ${name}`)
              return JSON.parse(sessionItem)
            }
            
            // 策略2: 从IndexedDB读取（永久存储）
            const cloudData = await localCloudService.loadFromFile(name)
            if (cloudData) {
              console.log(`✅ 从IndexedDB恢复: ${name}`)
              // 恢复到sessionStorage，加速后续读取
              try {
                sessionStorage.setItem(name, JSON.stringify(cloudData))
              } catch (e) {
                console.warn('恢复到sessionStorage失败:', e)
              }
              return cloudData
            }
            
            // 策略3: 兼容旧数据，尝试从localStorage读取（仅用于迁移）
            try {
              const legacyItem = localStorage.getItem(name)
              if (legacyItem) {
                console.log(`⚠️ 从localStorage读取旧数据，正在迁移...`)
                const parsed = JSON.parse(legacyItem)
                // 迁移到新存储
                sessionStorage.setItem(name, legacyItem)
                localCloudService.saveToFile(name, parsed).catch(e => console.warn('迁移失败:', e))
                return parsed
              }
            } catch (legacyError) {
              console.warn('读取localStorage失败（可忽略）:', legacyError)
            }
            
            return null
          } catch (error) {
            console.error('读取数据失败:', error)
            return null
          }
        },
        setItem: (name, value) => {
          // ⚡ 完全异步保存，不阻塞UI
          // 立即返回Promise，不等待任何操作
          setTimeout(() => {
            try {
              const dataString = JSON.stringify(value)
              const dataSize = new Blob([dataString]).size
              
              console.log(`📊 数据保存: ${(dataSize / 1024).toFixed(2)}KB`)
              
              // 策略1: 立即保存到sessionStorage（快速，容量大，不会满）
              try {
                sessionStorage.setItem(name, dataString)
                console.log('✅ 已保存到sessionStorage（主存储）')
              } catch (sessionError) {
                console.warn('⚠️ sessionStorage保存失败:', sessionError)
              }
              
              // 策略2: 异步保存到IndexedDB（永久存储，无限容量）
              console.log('🔄 后台保存到IndexedDB（永久存储）...')
              localCloudService.saveToFile(name, value)
                .then(() => console.log('✅ IndexedDB保存成功'))
                .catch(err => console.error('❌ IndexedDB保存失败:', err))
              
              // 不再使用localStorage（容量太小，经常满载，会阻塞UI）
            } catch (error) {
              console.error('❌ 序列化失败:', error)
            }
          }, 0)
          
          // 立即返回，不等待存储完成
          return Promise.resolve()
        },
        removeItem: (name) => {
          try {
            // 从所有存储中删除
            sessionStorage.removeItem(name)
            localStorage.removeItem(name) // 清理旧数据
            localCloudService.deleteFile(name).catch(e => console.warn('删除IndexedDB数据失败:', e))
            console.log(`🗑️ 已删除存储: ${name}`)
          } catch (error) {
            console.error('删除存储数据时出错:', error)
          }
        }
      }
    }
  )
)

// 页面卸载时保存数据
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    const store = useStoryStore.getState()
    if (store.beforeUnload) {
      store.beforeUnload()
    }
  })
  
  // 定期保存到sessionStorage（每30秒）
  setInterval(() => {
    try {
      const store = useStoryStore.getState()
      const { stories, characters, chapters, currentUserId } = store
      
      // 只在有数据时保存
      if (stories.length > 0) {
        const snapshot = {
          timestamp: Date.now(),
          userId: currentUserId,
          stories: stories,
          characters: characters,
          chapters: chapters
        }
        
        sessionStorage.setItem('altverse-state-snapshot', JSON.stringify(snapshot))
        console.log(`💾 状态快照已保存 (${stories.length} 个故事)`)
      }
    } catch (error) {
      console.error('保存状态快照失败:', error)
    }
  }, 30 * 1000) // 每30秒
}
